/**
 * ``useDismissAlert`` — TanStack Query mutation around the BFF's
 * ``POST /api/query/alert/{alert_id}/dismiss`` endpoint (REQ-QUERY-025;
 * ADR-029 §4 / §7). Operator/admin-only at the BFF; the Alerts panel
 * additionally hides the Dismiss control for non-dismissers (fail-safe
 * UX, CLAUDE.md §8.1).
 *
 * # What "dismiss" means (MVP)
 *
 * Per ADR-029 §6.2 / §11 the MVP semantic is **UI-side-only**: the alert
 * is removed from operators' views (here and — via the cross-session
 * ``alert-dismissed`` SSE fan-out — everywhere) but the source SAPIENT
 * Aggregator is NOT informed. The copy says "Dismiss" (not "Acknowledge")
 * to make that explicit to the operator.
 *
 * # Optimistic local removal + SSE convergence
 *
 * On success the mutation calls :func:`AlertsState.dismiss` to mark the
 * alert dismissed locally so it leaves *this* operator's panel
 * immediately — no wait for the round-trip echo. The BFF also fans the
 * dismissal out as an ``alert-dismissed`` SSE to every session (including
 * this one); :func:`alerts/use-alerts-sse-bridge` handles that, and
 * ``dismiss`` is idempotent (a no-op on an already-dismissed alert — see
 * :func:`alerts/alerts-store` §"Dismissal semantics"), so the two paths
 * converge without racing or double-applying.
 *
 * # Error surfacing (no retry on 403)
 *
 * The mutation surfaces ``isPending`` / ``error`` for the panel to render
 * a spinner / inline message. A 403 (the session lacks the dismiss role —
 * e.g., an analyst whose UI control was bypassed, or a role change
 * mid-session) is a definitive authorization denial, not a transient
 * fault: the mutation does NOT retry it. ``ApiError`` already carries the
 * status so the panel can distinguish "forbidden" from "server error".
 * (``apiFetch`` adds the CSRF header automatically on the POST per the
 * BFF's ``CsrfMiddleware``.)
 *
 * Cross-references:
 *   - REQ-UI-062 (Alerts panel + Dismiss).
 *   - REQ-QUERY-025 (operator dismissal).
 *   - REQ-AUTH-022 (BFF-side RBAC; the UI mirrors the role check).
 *   - ADR-029 §4 (dismissal_note) / §6.2 (Dismiss UX) / §7 (dismissal
 *     scope) / [MVP] item 6 (cross-session fan-out).
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { components } from "@/lib/api-types.gen";
import { useAlertsStore } from "@/alerts/alerts-store";

type AlertItem = components["schemas"]["AlertItem"];
type AlertDismissRequest = components["schemas"]["AlertDismissRequest"];

/** Variables for a single dismiss call. */
export interface DismissAlertVariables {
  /** The alert to dismiss. */
  readonly alertId: string;
  /** Optional operator-supplied dismissal note (ADR-029 §4). */
  readonly note?: string;
}

/**
 * Mutation hook to dismiss an alert.
 *
 * Returns the standard TanStack mutation result; call ``.mutate({
 * alertId })`` (optionally ``{ alertId, note }``) from the Dismiss
 * button. ``isPending`` drives the button's disabled/spinner state;
 * ``error`` (an :class:`ApiError`) drives the inline error message. On
 * success the returned :type:`AlertItem` carries the server's
 * authoritative dismissal columns, and the store is updated optimistically
 * from them.
 */
export function useDismissAlert(): UseMutationResult<AlertItem, ApiError, DismissAlertVariables> {
  return useMutation<AlertItem, ApiError, DismissAlertVariables>({
    mutationFn: async ({ alertId, note }) => {
      const body: AlertDismissRequest | undefined = note !== undefined ? { note } : undefined;
      return apiFetch<AlertItem>(`/api/query/alert/${encodeURIComponent(alertId)}/dismiss`, {
        method: "POST",
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    },
    // Optimistic local removal: mark dismissed from the server's
    // authoritative columns so this operator's panel drops the alert at
    // once. The cross-session alert-dismissed SSE follows and is a no-op
    // here (idempotent — see module docstring).
    onSuccess: (item) => {
      useAlertsStore.getState().dismiss(item.alert_id, {
        dismissedAt: item.dismissed_at ?? undefined,
        dismissedBy: item.dismissed_by,
        dismissalNote: item.dismissal_note,
      });
    },
    // A 403 is a definitive authorization denial — never retry it.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
